import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Paper, Title, Text, Group, Badge, Stack, Button, Loader, Center } from '@mantine/core';
import { IconSchool, IconUsers, IconEye, IconEdit } from '@tabler/icons-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { isRegistrationOpen, getActiveSeason } from '../../config/seasons';
import CollapsibleRoster from './CollapsibleRoster';

/**
 * SchoolAccountSection
 *
 * Renders on the profile page when the current user belongs to a school.
 * Shows this season's student count + a link into the studio portal
 * (view-only if the season is closed, editable while open).
 */
const SchoolAccountSection = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [season, setSeason] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }
      try {
        // Membership → school → this season's row → roster. Mirrors useSchool,
        // inlined because this card wants the roster too and the hook doesn't
        // load it.
        const { data: membership } = await supabase
          .from('school_members')
          .select('schools (*)')
          .eq('user_id', user.id)
          .maybeSingle();

        const sch = membership?.schools || null;
        if (cancelled) return;
        setAccount(sch);

        if (sch?.id) {
          const { data: seasonRows } = await supabase
            .from('school_seasons')
            .select('*')
            .eq('school_id', sch.id)
            .order('competition_year', { ascending: false });

          // Strictly the active season. Falling back to the newest row would
          // duplicate a past season that SchoolPastSeasonsSection already
          // shows, and would misreport an old roster as the current one.
          const seasonList = seasonRows || [];
          const showSeason =
            seasonList.find(x => x.competition_year === getActiveSeason().year) || null;
          if (cancelled) return;
          setSeason(showSeason);

          // A school with no season row yet (returning teacher, new year) has
          // no roster to fetch.
          if (showSeason?.id) {
            // Pull each student's info + variations in a single nested select
            // so the summary table can render everything without extra fetches.
            const { data: rows } = await supabase
              .from('registrations')
              .select(`
                id, status, payment_status, parent_consent_status,
                dancer_information ( first_name, last_name, date_of_birth ),
                variations ( id, variation_number, variation_name, music_file_url ),
                judging_reports ( id, variation_id, rank, published_at )
              `)
              .eq('school_season_id', showSeason.id)
              .eq('registration_type', 'school')
              .order('created_at', { ascending: true });
            if (!cancelled) setStudents(rows || []);
          }
        }
      } catch (err) {
        console.error('SchoolAccountSection load error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  if (loading) {
    return (
      <Paper radius="md" p="xl" withBorder style={{ borderTop: '4px solid var(--primary-gold)' }}>
        <Center><Loader size="sm" color="var(--primary-gold)" /></Center>
      </Paper>
    );
  }

  // Nothing to show if this user doesn't own a school account.
  if (!account) return null;

  const seasonYear = season?.competition_year || getActiveSeason().year;
  const seasonOpen = isRegistrationOpen(seasonYear);
  // No season row for the active year = a returning school that hasn't signed
  // up yet. That's the "Start your {year} roster" state, not "continue setup".
  const buttonLabel = !seasonOpen
    ? 'Open Studio Portal'
    : !season || season.competition_year !== getActiveSeason().year
      ? `Start your ${getActiveSeason().year} roster`
      : season.is_finalized ? 'Open Studio Portal' : 'Continue Setup';
  const buttonIcon = seasonOpen ? <IconEdit size={16} /> : <IconEye size={16} />;

  return (
    <Paper
      radius="md"
      p="xl"
      withBorder
      style={{ borderTop: '4px solid var(--primary-gold)' }}
    >
      <Group justify="space-between" mb="md">
        <Group>
          <IconSchool size={24} style={{ color: 'var(--primary-gold)' }} />
          <Title order={3} size="h4" style={{ color: 'var(--charcoal)' }}>
            Studio Portal
          </Title>
        </Group>
        <Group gap="xs">
          {account.status === 'pending_review' && (
            <Badge color="blue" size="lg" variant="light">Under review</Badge>
          )}
          {season?.is_finalized ? (
            <Badge color="green" size="lg">Finalized</Badge>
          ) : (
            <Badge color="yellow" size="lg" variant="light">In Progress</Badge>
          )}
        </Group>
      </Group>

      <Stack spacing="sm" mb="xl">
        <Group justify="space-between">
          <Text size="sm" color="dimmed">School:</Text>
          <Text size="sm" weight={600}>{account.name || '—'}</Text>
        </Group>
        <Group justify="space-between">
          <Text size="sm" color="dimmed">Competition:</Text>
          <Text size="sm" weight={600}>Prix du Canada {seasonYear}</Text>
        </Group>
        <Group justify="space-between">
          <Text size="sm" color="dimmed">Students registered:</Text>
          <Badge color="yellow" variant="light" leftSection={<IconUsers size={12} />}>
            {students.length}
          </Badge>
        </Group>
      </Stack>

      <Button
        fullWidth
        leftSection={buttonIcon}
        onClick={() => navigate('/register/school/students')}
        style={{ background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))' }}
      >
        {buttonLabel}
      </Button>

      {students.length > 0 && (
        <CollapsibleRoster students={students} year={seasonYear} />
      )}
    </Paper>
  );
};

export default SchoolAccountSection;
